/**
 * The chat state a runner transcript folds into, and the reducer that
 * folds it. Pure: `applyEvent` returns a new state and never touches the
 * DOM, the network, or product copy. Applications render `ChatState` in
 * whatever component tree they own.
 *
 * Besides runner events the reducer takes a few client-side events an
 * application dispatches itself: the turn mode it read from the response
 * header, input records it fetched, sets it froze, a pending message it
 * retracts after a failed send, and errors from its own HTTP calls.
 */
import { chatError, errorFromStreamReason } from './errors.js';
import { contentBlocks, endStatus as readEndStatus, eventTimestamp, eventType, eventUuid, isToolResultEvent, messageAttachments, messageForms, messageText, stripInputAnchors, } from './events.js';
export function initialChatState() {
    return {
        sessionId: null,
        harness: null,
        messages: [],
        activities: [],
        streamItemId: null,
        turnOpen: false,
        turnMode: null,
        endStatus: null,
        error: null,
        inputs: {},
        frozenSetIds: [],
        seenUuids: new Set(),
    };
}
function str(value) {
    return typeof value === 'string' ? value : undefined;
}
function finalizeInterim(messages) {
    if (!messages.some((message) => message.interim))
        return messages;
    return messages.map((message) => (message.interim ? { ...message, interim: false } : message));
}
function dropInterim(messages) {
    return messages.some((message) => message.interim) ? messages.filter((message) => !message.interim) : messages;
}
/**
 * A live bubble whose item never completed is commentary the harness
 * withheld from the transcript (Codex streams its interim notes but only
 * the last agent message becomes an `assistant` event). When another item
 * starts streaming, the note leaves the message list and its text becomes
 * the status line until the turn ends.
 */
function supersedeInterim(state, itemId) {
    const stale = state.messages.filter((message) => message.interim && message.itemId !== itemId);
    if (stale.length === 0)
        return state;
    const activities = state.activities.slice();
    const turnId = latestUserMessageId(state.messages);
    for (const note of stale) {
        const text = note.text.trim();
        if (!text)
            continue;
        const index = activities.findIndex((activity) => activity.id === note.id);
        if (index >= 0)
            activities[index] = { ...activities[index], text };
        else
            activities.push({ id: note.id, name: 'progress', text, status: 'completed', turnId,
                ...(note.phase !== 'final_answer' ? { source: 'commentary' } : {}) });
    }
    return { ...state, messages: state.messages.filter((message) => !stale.includes(message)), activities };
}
/** A following tool establishes that unclassified live text preceded an action.
 * Explicit final answers are never reclassified, including corrective tool turns. */
function markCommentaryBeforeTool(state) {
    const boundary = state.messages.findLastIndex(message => message.role === 'user');
    let changed = false;
    const messages = state.messages.map((message, index) => {
        if (index <= boundary || !message.interim || message.phase)
            return message;
        changed = true;
        return { ...message, phase: 'commentary', commentaryComplete: true };
    });
    return changed ? { ...state, messages } : state;
}
/** The user message the live tools belong to: the newest one, pending or echoed. */
export function latestUserMessageId(messages) {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        if (messages[index].role === 'user')
            return messages[index].id;
    }
    return undefined;
}
function activityInput(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    return Object.keys(value).length > 0 ? value : undefined;
}
function upsertActivity(state, id, name, input) {
    const { activities } = state;
    const index = activities.findIndex((activity) => activity.id === id);
    if (index < 0) {
        const entry = { id, name, status: 'running', turnId: latestUserMessageId(state.messages) };
        if (input)
            entry.input = input;
        return [...activities, entry];
    }
    const current = activities[index];
    if (current.name === name && (!input || current.input === input))
        return activities;
    const updated = activities.slice();
    updated[index] = input ? { ...current, name, input } : { ...current, name };
    return updated;
}
/** Every `tool_use` block of a message opens a running step, or fills in
 * the input of one the harness announced earlier. */
function toolUseActivities(state, message) {
    let activities = state.activities;
    for (const block of contentBlocks(message)) {
        if (block.type !== 'tool_use' || typeof block.id !== 'string' || typeof block.name !== 'string')
            continue;
        activities = upsertActivity({ ...state, activities }, block.id, block.name, activityInput(block.input));
    }
    return activities;
}
/** Every `tool_result` block of a message settles the step it answers. */
function settleActivities(activities, message) {
    let updated = activities;
    for (const block of contentBlocks(message)) {
        if (block.type !== 'tool_result' || typeof block.tool_use_id !== 'string')
            continue;
        const index = updated.findIndex((activity) => activity.id === block.tool_use_id);
        if (index < 0)
            continue;
        const status = block.is_error === true ? 'error' : 'completed';
        if (updated[index].status === status)
            continue;
        if (updated === activities)
            updated = activities.slice();
        updated[index] = { ...updated[index], status };
    }
    return updated;
}
function completeRunning(activities) {
    if (!activities.some((activity) => activity.status === 'running'))
        return activities;
    return activities.map((activity) => (activity.status === 'running' ? { ...activity, status: 'completed' } : activity));
}
/** When an optimistic message takes its echo's uuid, its steps follow. */
function rekeyTurn(activities, from, to) {
    if (from === to || !activities.some((activity) => activity.turnId === from))
        return activities;
    return activities.map((activity) => (activity.turnId === from ? { ...activity, turnId: to } : activity));
}
function normalizeText(text) {
    return text.replace(/\r\n?/g, '\n').trim();
}
/**
 * The optimistic message a runner echo replaces. The echo's text may differ
 * in line endings or surrounding whitespace once the backend stripped its
 * own prompt wrappers. Identified sends only match that exact identity;
 * legacy unidentified sends use normalized text, then the newest legacy
 * pending message. Unrelated activity must not acknowledge an identified send.
 */
function pendingEchoIndex(messages, text, requestId) {
    if (requestId)
        return messages.findIndex(candidate => candidate.requestId === requestId);
    const wanted = normalizeText(text);
    const exact = messages.findIndex((candidate) => candidate.pending && !candidate.requestId && normalizeText(candidate.text) === wanted);
    if (exact >= 0)
        return exact;
    return messages.findLastIndex((candidate) => candidate.pending && !candidate.requestId);
}
function registerForms(state, forms) {
    if (forms.length === 0)
        return state.inputs;
    const inputs = { ...state.inputs };
    for (const form of forms) {
        if (inputs[form.requestId])
            continue;
        inputs[form.requestId] = {
            setId: form.setId,
            outcome: null,
            value: null,
            frozen: state.frozenSetIds.includes(form.setId),
        };
    }
    return inputs;
}
/**
 * Record how the runner took the message. The same fold serves the
 * `turn_mode` the connect event carries and the local `turn_mode` event an
 * application dispatches from the response header, so applying both is
 * harmless.
 */
function applyTurnMode(state, mode) {
    return {
        ...state,
        turnMode: mode,
        turnOpen: true,
        messages: mode === 'started' ? finalizeInterim(state.messages) : state.messages,
    };
}
export function applyEvent(state, event) {
    const kind = eventType(event);
    if (!kind)
        return state;
    const uuid = eventUuid(event);
    if (uuid && state.seenUuids.has(uuid))
        return state;
    const seenUuids = uuid ? new Set(state.seenUuids).add(uuid) : state.seenUuids;
    return reduce(state, event, kind, uuid, seenUuids);
}
/**
 * Fold a batch of events. Same result as `applyEvent` in sequence, but the
 * seen-uuid set is copied once for the batch instead of once per event, so
 * a page or a burst of deltas costs linear time. Intermediate states are
 * never exposed; the input state is not touched.
 */
export function applyEvents(state, events) {
    let current = state;
    let seen = null;
    for (const event of events) {
        const kind = eventType(event);
        if (!kind)
            continue;
        const uuid = eventUuid(event);
        if (uuid) {
            if ((seen ?? current.seenUuids).has(uuid))
                continue;
            seen ??= new Set(current.seenUuids);
            seen.add(uuid);
        }
        current = reduce(current, event, kind, uuid, seen ?? current.seenUuids);
    }
    return current;
}
function reduce(state, raw, kind, uuid, seenUuids) {
    const next = seenUuids === state.seenUuids ? state : { ...state, seenUuids };
    switch (kind) {
        case 'connect': {
            // A POST's connect carries how the message was taken; a resume GET's
            // does not, and older runners send neither.
            const mode = raw.turn_mode === 'started' || raw.turn_mode === 'steered' ? raw.turn_mode : null;
            return mode ? applyTurnMode(next, mode) : next;
        }
        case 'init': {
            const harness = str(raw.harness);
            return {
                ...next,
                sessionId: str(raw.session_id) ?? next.sessionId,
                harness: harness ?? next.harness,
                turnOpen: true,
                endStatus: null,
                error: null,
            };
        }
        case 'user': {
            const message = raw.message;
            const settled = settleActivities(next.activities, message);
            const withResults = settled === next.activities ? next : { ...next, activities: settled };
            if (isToolResultEvent(raw))
                return withResults;
            const text = messageText(message);
            const attachments = messageAttachments(message);
            const pending = Boolean(message?.pending);
            const requestId = typeof raw.request_id === 'string' && raw.request_id ? raw.request_id : undefined;
            const entry = {
                id: uuid ?? `user-${next.messages.length}`,
                uuid: uuid ?? undefined,
                role: 'user',
                ...(requestId ? { requestId } : {}),
                text,
                pending,
                attachments,
                forms: [],
                timestamp: eventTimestamp(raw),
            };
            let messages = withResults.messages;
            if (!pending) {
                const index = pendingEchoIndex(messages, text, requestId);
                if (index >= 0) {
                    const replaced = messages[index];
                    messages = messages.slice();
                    messages[index] = entry;
                    return {
                        ...withResults,
                        messages,
                        activities: rekeyTurn(withResults.activities, replaced.id, entry.id),
                        turnOpen: true,
                        endStatus: null,
                        error: null,
                    };
                }
            }
            return {
                ...withResults,
                messages: [...messages, entry],
                turnOpen: true,
                endStatus: pending ? withResults.endStatus : null,
                error: pending ? withResults.error : null,
            };
        }
        case 'assistant_delta': {
            const text = str(raw.text) ?? '';
            const itemId = str(raw.item_id) ?? next.streamItemId ?? 'stream';
            const phase = raw.phase === 'commentary' || raw.phase === 'final_answer' ? raw.phase : undefined;
            const current = supersedeInterim(next, itemId);
            const messages = current.messages.slice();
            const last = messages[messages.length - 1];
            if (last && last.interim && last.itemId === itemId) {
                messages[messages.length - 1] = { ...last, text: last.text + text, ...(phase ? { phase } : {}) };
            }
            else {
                messages.push({
                    id: `item:${itemId}`,
                    itemId,
                    ...(phase ? { phase } : {}),
                    role: 'assistant',
                    text,
                    interim: true,
                    attachments: [],
                    forms: [],
                    timestamp: eventTimestamp(raw),
                });
            }
            return { ...current, messages, streamItemId: itemId };
        }
        case 'assistant': {
            const activities = toolUseActivities(next, raw.message);
            const withActivities = activities === next.activities ? next : { ...next, activities };
            const withTools = contentBlocks(raw.message).some(block => block.type === 'tool_use')
                ? markCommentaryBeforeTool(withActivities) : withActivities;
            const text = messageText(raw.message);
            const forms = messageForms(raw.message);
            const attachments = messageAttachments(raw.message);
            // A message that draws nothing (tool calls only, or anchors whose forms
            // never came) is activity, not a bubble; the timeline shows it.
            if (!stripInputAnchors(text) && forms.length === 0 && attachments.length === 0)
                return withTools;
            const entry = {
                id: uuid ?? `assistant-${withTools.messages.length}`,
                uuid: uuid ?? undefined,
                itemId: str(raw.item_id) ?? withTools.streamItemId ?? undefined,
                ...(raw.phase === 'final_answer' || raw.phase === 'commentary' ? { phase: raw.phase } : {}),
                role: 'assistant',
                text,
                attachments,
                forms,
                timestamp: eventTimestamp(raw),
            };
            const messages = withTools.messages.slice();
            const interimIndex = messages.findLastIndex((message) => message.interim);
            if (interimIndex >= 0)
                messages[interimIndex] = entry;
            else
                messages.push(entry);
            return { ...withTools, messages, streamItemId: null, inputs: registerForms(withTools, forms) };
        }
        case 'tool_started': {
            const name = str(raw.name) ?? 'tool';
            const id = str(raw.id) ?? `${name}-${next.activities.length}`;
            const marked = markCommentaryBeforeTool(next);
            const activities = upsertActivity(marked, id, name);
            return activities === marked.activities ? marked : { ...marked, activities };
        }
        case 'progress': {
            const text = str(raw.text)?.trim();
            const id = str(raw.id) ?? str(raw.uuid) ?? 'progress';
            const turnId = latestUserMessageId(next.messages);
            const activities = next.activities.slice();
            const index = activities.findIndex((activity) => activity.id === id && activity.turnId === turnId);
            if (index >= 0)
                activities[index] = { ...activities[index], text };
            else if (text) {
                activities.push({ id, name: 'progress', text, status: 'completed', turnId,
                    ...(raw.source === 'commentary' ? { source: 'commentary' } : {}) });
            }
            else
                return next;
            const messages = raw.source === 'commentary'
                ? next.messages.filter(message => !(message.interim && message.itemId === id)) : next.messages;
            return { ...next, messages, activities };
        }
        case 'error':
            return { ...next, error: errorFromStreamReason(raw.reason, raw.message) };
        case 'end': {
            const status = readEndStatus(raw);
            const error = next.error ?? (status === 'failed' ? chatError('turn_failed') : null);
            // A successful turn completed every message it kept; a bubble still
            // interim was withheld commentary and is not in the transcript. A
            // cancelled or failed turn keeps what arrived before it stopped.
            let messages = status === 'success' ? dropInterim(next.messages) : finalizeInterim(next.messages);
            const userIndex = messages.findLastIndex((message) => message.role === 'user' && !message.pending);
            if (userIndex >= 0 && !messages[userIndex].endStatus) {
                messages = messages.slice();
                messages[userIndex] = { ...messages[userIndex], endStatus: status };
            }
            return {
                ...next,
                messages,
                activities: completeRunning(next.activities),
                streamItemId: null,
                turnOpen: false,
                endStatus: status,
                error,
            };
        }
        case 'turn_mode':
            return applyTurnMode(next, raw.mode === 'steered' ? 'steered' : 'started');
        case 'input_record': {
            const record = raw.record;
            if (!record || typeof record.request_id !== 'string' || typeof record.set_id !== 'string')
                return next;
            const outcome = record.outcome ?? null;
            return {
                ...next,
                inputs: {
                    ...next.inputs,
                    [record.request_id]: {
                        setId: record.set_id,
                        outcome: outcome === 'submit' || outcome === 'decline' || outcome === 'cancel' ? outcome : null,
                        value: record.value ?? null,
                        frozen: Boolean(record.frozen_at) || next.frozenSetIds.includes(record.set_id),
                    },
                },
            };
        }
        case 'input_sets_frozen': {
            const setIds = Array.isArray(raw.setIds) ? raw.setIds.filter((id) => typeof id === 'string') : [];
            const fresh = setIds.filter((id) => !next.frozenSetIds.includes(id));
            if (fresh.length === 0)
                return next;
            const frozen = new Set([...next.frozenSetIds, ...fresh]);
            const inputs = {};
            for (const [requestId, answer] of Object.entries(next.inputs)) {
                inputs[requestId] = frozen.has(answer.setId) ? { ...answer, frozen: true } : answer;
            }
            return { ...next, frozenSetIds: [...frozen], inputs };
        }
        case 'retract_pending':
            return { ...next, messages: next.messages.filter((message) => !message.pending) };
        case 'chat_error':
            return { ...next, error: raw.error ?? chatError('unknown') };
        case 'clear_error':
            return next.error ? { ...next, error: null } : next;
        default:
            return next;
    }
}
/**
 * Fold an older transcript page in front of what is rendered. Events the
 * state already saw are skipped; its steps join the front of the timeline;
 * turn flags and errors stay as they are, because the older page says
 * nothing about the live turn.
 */
export function prependEvents(state, events) {
    const unseen = [...events].filter((event) => {
        const uuid = eventUuid(event);
        return !(uuid && state.seenUuids.has(uuid));
    });
    if (unseen.length === 0)
        return state;
    const older = applyEvents({ ...initialChatState(), sessionId: state.sessionId, harness: state.harness, frozenSetIds: state.frozenSetIds }, unseen);
    const activityKey = (activity) => JSON.stringify([activity.turnId, activity.id]);
    const known = new Set(state.activities.map(activityKey));
    const olderActivities = older.activities.filter((activity) => !known.has(activityKey(activity)));
    return {
        ...state,
        messages: [...older.messages, ...state.messages],
        activities: olderActivities.length ? [...olderActivities, ...state.activities] : state.activities,
        inputs: { ...older.inputs, ...state.inputs },
        seenUuids: new Set([...older.seenUuids, ...state.seenUuids]),
    };
}
/** Set ids of rendered forms that are not frozen yet: freeze these before sending. */
export function openInputSetIdsOf(state) {
    const open = [];
    for (const answer of Object.values(state.inputs)) {
        if (!answer.frozen && !open.includes(answer.setId))
            open.push(answer.setId);
    }
    return open;
}
/** The event that marks every rendered set frozen once the send succeeded. */
export function freezeRenderedInputs(state) {
    return { type: 'input_sets_frozen', setIds: openInputSetIdsOf(state) };
}
/**
 * The newest step of the live turn, for a client that shows one status
 * line; null once the turn ended or while no step of this turn arrived.
 */
export function currentActivity(state) {
    if (!state.turnOpen)
        return null;
    const turnId = latestUserMessageId(state.messages);
    for (let index = state.activities.length - 1; index >= 0; index -= 1) {
        if (state.activities[index].turnId === turnId)
            return state.activities[index];
    }
    return null;
}
/** A public note for an optional compact UI. Never selects generic progress,
 * reasoning summaries or tool inputs. A streaming answer takes precedence. */
export function currentCommentary(state) {
    if (!state.turnOpen || state.error)
        return null;
    const boundary = state.messages.findLastIndex(message => message.role === 'user');
    for (let index = state.messages.length - 1; index > boundary; index -= 1) {
        const message = state.messages[index];
        if (message.role !== 'assistant')
            continue;
        if (message.phase !== 'commentary')
            return null;
        if (message.commentaryComplete && message.text.trim())
            return message.text;
    }
    const turnId = state.messages[boundary]?.id;
    for (let index = state.activities.length - 1; index >= 0; index -= 1) {
        const activity = state.activities[index];
        if (activity.turnId === turnId && activity.source === 'commentary' && activity.text?.trim())
            return activity.text;
    }
    return null;
}
